import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, FileSpreadsheet, FileCheck2, Clock, ScanLine, Sparkles } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import SiteFooter from '../components/SiteFooter';

const BENEFITS = [
  { icon: Clock, title: 'Less time chasing receipts', desc: 'Clients capture expenses as they happen on WhatsApp, so you stop re-requesting missing paperwork.' },
  { icon: FileCheck2, title: 'Organized client expense records', desc: 'Every expense arrives categorized to the right tax line, with the receipt attached and archived.' },
  { icon: FileSpreadsheet, title: 'Downloadable tax-ready reports', desc: 'Clients export clean CSV, Excel and PDF reports for any date range — ready for your workpapers.' },
  { icon: ScanLine, title: 'Clients use familiar WhatsApp', desc: 'No new app for your clients to learn — they capture receipts in the chat they already use every day.' },
  { icon: Users, title: 'Works with your accounting software', desc: 'MoneyFeed organizes the source records that feed QuickBooks, Xero or your own workflow — it does not replace them or your tax prep.' },
];

const FAQS = [
  { q: 'How does MoneyFeed help accountants and bookkeepers?', a: 'Your clients capture income, expenses, receipts and mileage on WhatsApp throughout the year. You get clean, categorized, tax-ready data you can export to CSV or Excel — dramatically cutting data-entry and chasing receipts.' },
  { q: 'Can I export a client\'s data for my own software?', a: 'Yes. The export center produces standard CSV and Excel files filtered by date range, type and category, so you can import into your existing workflow or hand them to a colleague.' },
  { q: 'Is there an audit trail?', a: 'Every scanned receipt is stored and linked to its transaction, giving you defensible proof for the tax authority.' },
];

export default function ForAccountants() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#F5F3FF] to-[#D1FAE5]" data-testid="for-accountants-page">
      <Seo
        title="Client Receipts & AI Expense Reports for Accountants | MoneyFeed"
        description="Your clients snap receipts on WhatsApp or the app, MoneyFeed's AI categorizes every expense, and you get accountant-ready CSV, Excel and PDF reports."
        path="/for-accountants"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/10 rounded-full mb-5">
          <FileCheck2 className="h-4 w-4 text-[#7C3AED]" />
          <span className="text-xs font-bold text-[#7C3AED] uppercase tracking-wider">For Accountants & Bookkeepers</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#064E3B] leading-tight mb-5 max-w-4xl mx-auto" data-testid="accountants-h1">
          Client receipts, categorized by AI into <span className="text-[#7C3AED]">accountant-ready reports</span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-testid="accountants-subhead">
          MoneyFeed makes bookkeeping effortless for your clients and accounting easier for you—without changing the software you already use.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto">
          <Link to="/auth" className="w-full sm:w-auto">
            <Button className="w-full sm:w-[230px] border border-transparent bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="accountants-get-started">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <Link to="/export-to-excel" className="w-full sm:w-auto">
            <Button variant="outline" className="w-full sm:w-[230px] border-[#064E3B]/30 text-[#064E3B] hover:bg-[#064E3B]/5 font-bold rounded-full px-7 py-6 text-base" data-testid="accountants-export-link">
              See the export center
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12" data-testid="accountants-workflow">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">The client-to-accountant workflow</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: '1', icon: ScanLine, title: 'Upload receipts via App or WhatsApp', desc: 'Clients snap receipts in the MoneyFeed app or message them on WhatsApp — in the moment, all year long.' },
            { n: '2', icon: Sparkles, title: 'AI categorizes expenses', desc: 'MoneyFeed reads each receipt and maps the expense to the correct tax line automatically — no manual spreadsheets.' },
            { n: '3', icon: FileSpreadsheet, title: 'Export CSV / Excel / PDF', desc: 'Pull clean, categorized, tax-ready reports for any client and date range in one click.' },
            { n: '4', icon: FileCheck2, title: 'Share with accountant', desc: 'Clients hand you ready-to-file records that drop straight into the accounting software you already use.' },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-2xl shadow-md p-6" data-testid={`accountants-step-${s.n}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-[#7C3AED] text-white font-bold flex items-center justify-center flex-shrink-0">{s.n}</div>
                <div className="w-11 h-11 rounded-xl bg-[#F5F3FF] flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-[#7C3AED]" />
                </div>
              </div>
              <h3 className="font-bold text-[#064E3B] mb-1">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/demo">
            <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-full px-8 py-6 text-base" data-testid="accountants-book-demo">
              Book a Demo <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Why CPAs partner with MoneyFeed</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`accountants-benefit-${b.title}`}>
              <div className="w-11 h-11 rounded-xl bg-[#F5F3FF] flex items-center justify-center mb-4">
                <b.icon className="h-5 w-5 text-[#7C3AED]" />
              </div>
              <h3 className="font-bold text-[#064E3B] mb-1">{b.title}</h3>
              <p className="text-gray-600 text-sm">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12" data-testid="accountants-partner">
        <div className="bg-[#064E3B] rounded-3xl p-8 sm:p-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full mb-5">
            <Users className="h-4 w-4 text-[#A7F3D0]" />
            <span className="text-xs font-bold text-[#A7F3D0] uppercase tracking-wider">CPA Partner Program</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Grow Your Practice with MoneyFeed</h2>
          <p className="text-[#D1FAE5] mb-8 max-w-2xl mx-auto">
            Recommend an easier way for your clients to capture receipts and organize business expenses. Explore MoneyFeed partnership opportunities for eligible Pro and Business subscriptions.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto mb-8">
            {[
              'Free and Plus plans are excluded from the partnership commission program.',
              'The Pro CPA partner offer is based on annual subscriptions.',
              'Business partner subscriptions use custom annual pricing.',
              'Referral commissions are subject to partner eligibility and approved written terms.',
              'Co-branded and white-label arrangements are exploratory and not yet available as products.',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-[#A7F3D0]" data-testid={`partner-detail-${i}`}>
                <FileCheck2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#84CC16]" />
                <span>{t}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/partners/accountants">
              <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="accountants-partner-cta">
                Become a MoneyFeed CPA Partner <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="accountants-partner-demo">
                See How MoneyFeed Works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Questions from firms</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`accountants-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
